import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, RiskBadge } from "@/components/common/Bits";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Cpu, MessageSquareText, ServerCog, Puzzle, Bot } from "lucide-react";

const KINDS = [
  { key: "models", label: "Model Registry", icon: Cpu },
  { key: "prompts", label: "Prompt Registry", icon: MessageSquareText },
  { key: "mcp", label: "MCP Registry", icon: ServerCog },
  { key: "skills", label: "Skills Library", icon: Puzzle },
  { key: "agents", label: "Agent Registry", icon: Bot },
];

const Chip = ({ children }) => <span className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground text-[10px] font-medium">{children}</span>;
const Row = ({ label, value }) => <p className="text-xs mt-1"><span className="text-muted-foreground">{label}: </span>{value ?? "—"}</p>;

export default function Registries() {
  const [data, setData] = useState({});

  useEffect(() => {
    KINDS.forEach(({ key }) => {
      api.get(`/registry/${key}`).then(({ data: rows }) => setData((p) => ({ ...p, [key]: rows })));
    });
  }, []);

  return (
    <div>
      <PageHeader title="Registros especializados" subtitle="Model Registry, Prompt Registry, MCP Registry, Skills Library y Agent Registry del ecosistema institucional." testId="registries-header" />
      <Tabs defaultValue="models">
        <TabsList className="bg-muted flex-wrap h-auto">
          {KINDS.map((k) => <TabsTrigger key={k.key} value={k.key} data-testid={`registry-tab-${k.key}`} className="text-xs sm:text-sm"><k.icon className="h-3.5 w-3.5 mr-1.5" />{k.label}</TabsTrigger>)}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key} className="mt-5">
            {!data[k.key] ? <div className="grid sm:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}</div> : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {data[k.key].map((r) => (
                  <Card key={r.id} className="shadow-card border-border flex flex-col" data-testid={`registry-item-${r.id}`}>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0"><k.icon className="h-4 w-4" /></div>
                          <div className="min-w-0">
                            <p className="font-display font-semibold text-sm leading-tight truncate">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{r.provider || r.owner || r.purpose}</p>
                          </div>
                        </div>
                        <StatusBadge status={r.status || r.approval_status} />
                      </div>

                      <div className="mt-3 flex-1">
                        {k.key === "models" && (<>
                          <Row label="Versión" value={r.version} />
                          <div className="flex flex-wrap gap-1.5 mt-2">{(r.capabilities || []).map((c) => <Chip key={c}>{c}</Chip>)}</div>
                          <Row label="Restricciones" value={r.restrictions} />
                          <Row label="Riesgos conocidos" value={r.known_risks} />
                          <Row label="Iniciativas que lo usan" value={r.initiatives_count} />
                        </>)}
                        {k.key === "prompts" && (<>
                          <Row label="Propósito" value={r.purpose} />
                          <Row label="Modelo" value={r.related_model} />
                          <Row label="Versión" value={r.version} />
                          <div className="flex flex-wrap gap-1.5 mt-2">{(r.variables || []).map((v) => <Chip key={v}>{`{${v}}`}</Chip>)}</div>
                          <Row label="Evaluaciones" value={r.evaluations} />
                        </>)}
                        {k.key === "mcp" && (<>
                          <Row label="Propósito" value={r.purpose} />
                          <div className="flex flex-wrap gap-1.5 mt-2">{(r.exposed_tools || []).map((t) => <Chip key={t}>{t}</Chip>)}</div>
                          <Row label="Datos accesibles" value={r.accessible_data} />
                          <Row label="Autenticación" value={r.authentication} />
                          <div className="mt-2"><RiskBadge level={r.risk} /></div>
                        </>)}
                        {k.key === "skills" && (<>
                          <Row label="Descripción" value={r.description} />
                          <div className="flex flex-wrap gap-1.5 mt-2">{(r.systems || []).map((s) => <Chip key={s}>{s}</Chip>)}</div>
                          <Row label="Permisos" value={r.permissions} />
                          <Row label="Versión" value={r.version} />
                        </>)}
                        {k.key === "agents" && (<>
                          <Row label="Propósito" value={r.purpose} />
                          <Row label="Modelo" value={r.model} />
                          <Row label="Autonomía" value={r.autonomy_level} />
                          <Row label="Límites" value={r.limits} />
                          <div className="flex flex-wrap gap-1.5 mt-2">{(r.controls || []).map((c) => <Chip key={c}>{c}</Chip>)}</div>
                        </>)}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border">Owner: {r.owner || "Dirección de Gobernanza de IA"}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
